import type { ModuleManifest } from '@confluent/shared/modules';
import type React from 'react';
import type { RouteObject } from 'react-router-dom';
import type { CardRenderer } from '../components/conversations/cards/registry.js';
import type { PanelTypeDefinition } from '../components/context-panel/registry.js';
import type { Project, ProjectCommandCenter } from '../hooks/useProjects.js';

export interface ArtifactPreviewSummary {
  id: string;
  kind: string;
  metadata?: { custom?: Record<string, unknown> } | null;
}

export interface ArtifactPreviewRendererProps {
  artifactId: string;
  artifact: ArtifactPreviewSummary;
}

/**
 * A lazy-loaded widget that a module contributes to dashboard surfaces.
 * The shell resolves which widgets to render based on `suitableFor` and `policy`.
 */
export interface ClientWidgetContribution {
  /** Stable identifier, scoped to the module. e.g. 'finance.risk-status' */
  id: string;
  /** Factory that returns the widget component via dynamic import. */
  lazy: () => Promise<{ default: React.ComponentType }>;
  /**
   * Dashboard surface keys this widget may appear on.
   * e.g. ['dashboard.trading', 'dashboard.overview'].
   * Omit (or use empty array) to allow all surfaces.
   */
  suitableFor?: string[];
  /** Named policy that must pass (AND'd with module enablement). */
  policy?: string;
  /** Default display title for the widget. */
  title?: string;
}

/**
 * Metadata-only preview renderer that a module contributes for artifact rows.
 * Core owns artifact listing and full artifact rendering; modules own
 * domain-specific preview semantics.
 */
export interface ClientArtifactPreviewContribution {
  /** Stable identifier, scoped to the module. e.g. 'finance.conversation-artifact-preview' */
  id: string;
  /** User-facing toggle label. Defaults to "Preview". */
  label?: string;
  /** Return true when this renderer can preview the artifact summary row. */
  matches: (artifact: ArtifactPreviewSummary) => boolean;
  /** Lazy renderer for the module-specific metadata-only preview. */
  lazy: () => Promise<{ default: React.ComponentType<ArtifactPreviewRendererProps> }>;
}

/**
 * A conversation card renderer a module contributes for its own card types.
 * Registered against the module's id, so a card whose `moduleId` matches gets
 * the module renderer while other modules keep the core one.
 */
export interface ClientCardContribution {
  /** Card `type` from the shared card contract, e.g. 'strategy_detail'. */
  cardType: string;
  /** Factory that returns the renderer component via dynamic import. */
  lazy: () => Promise<{ default: CardRenderer }>;
}

/** Props every project panel receives from the project detail screen. */
export interface ProjectPanelProps {
  project: Project;
  commandCenter: ProjectCommandCenter | undefined;
}

/**
 * A section of the unified project screen that a module contributes. Served at
 * `/projects/:id/<id>`; shown only to organizations that have `moduleId` enabled.
 */
export interface ClientProjectPanelContribution {
  /** Section path segment, e.g. 'code' (`/projects/:id/code`). */
  id: string;
  /** User-facing section name. */
  label: string;
  /** Module whose enablement gates the panel. */
  moduleId: string;
  /** Named policy that must pass (AND'd with module enablement). */
  policy?: string;
  /** Factory that returns the panel component via dynamic import. */
  lazy: () => Promise<{ default: React.ComponentType<ProjectPanelProps> }>;
}

/** Props every workstream panel receives from the workstream detail view. */
export interface WorkstreamPanelProps {
  projectId: number;
  workstreamId: number;
}

/**
 * A card of the workstream detail view that a module contributes; shown only to organizations
 * that have `moduleId` enabled.
 */
export interface ClientWorkstreamPanelContribution {
  id: string;
  /** Module whose enablement gates the panel. */
  moduleId: string;
  /** Named policy that must pass (AND'd with module enablement). */
  policy?: string;
  /** Factory that returns the panel component via dynamic import. */
  lazy: () => Promise<{ default: React.ComponentType<WorkstreamPanelProps> }>;
}

// Client-side module definition. Contributes lazy-loaded routes, nav items,
// and optional widgets to the app shell. The shell filters by the org's
// enabled modules returned from GET /api/me/modules.
export interface ClientModule {
  manifest: ModuleManifest;
  routes: RouteObject[];
  /** Dashboard widget contributions. Phase E wires these into WidgetGrid. */
  widgets?: ClientWidgetContribution[];
  /** Metadata-only artifact preview contributions, rendered by generic artifact surfaces. */
  artifactPreviews?: ClientArtifactPreviewContribution[];
  /** Conversation card renderers for card types this module owns. */
  cards?: ClientCardContribution[];
  /** Sections of the unified project screen this module contributes. */
  projectPanels?: ClientProjectPanelContribution[];
  /** Cards of the workstream detail view this module contributes. */
  workstreamPanels?: ClientWorkstreamPanelContribution[];
  /**
   * Side-panel types for this module's records (`?panel=<type>:<id>`, Spec 76), named after the card `entity` they
   * open: a conversation shows the record inside it instead of in the detailed view (owner 2026-10-31).
   */
  panelTypes?: PanelTypeDefinition[];
}

export class ClientModuleRegistry {
  private readonly modules = new Map<string, ClientModule>();

  register(module: ClientModule): void {
    if (this.modules.has(module.manifest.id)) {
      throw new Error(`Client module already registered: ${module.manifest.id}`);
    }
    this.modules.set(module.manifest.id, module);
  }

  get(moduleId: string): ClientModule | undefined {
    return this.modules.get(moduleId);
  }

  all(): ClientModule[] {
    return [...this.modules.values()].sort((a, b) => a.manifest.navOrder - b.manifest.navOrder);
  }

  filter(enabledModuleIds: Set<string>): ClientModule[] {
    return this.all().filter((m) => enabledModuleIds.has(m.manifest.id));
  }
}

export const clientModuleRegistry = new ClientModuleRegistry();
